import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { CanvasControls, type FocusTarget } from "@/components/canvas-focus-control";

export type { FocusTarget } from "@/components/canvas-focus-control";

export interface WorldRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Screen-space margins kept clear of fixed UI (toolbar, Next Steps) when framing content. */
export interface ViewInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface JournalCanvasApi {
  /** The part of the world currently visible, in world px. */
  getViewWorldRect(): WorldRect;
  /** A screen point (clientX/clientY) in world px. */
  clientToWorld(x: number, y: number): { x: number; y: number };
  /** Converts a screen-space DOMRect (e.g. from getBoundingClientRect) to world px. */
  clientRectToWorld(r: DOMRect): WorldRect;
  /** World-space bounds of every canvas item plus `extra` (e.g. sketch strokes). */
  getContentBounds(): WorldRect | null;
}

export const CANVAS_MIN_SCALE = 0.25;
export const CANVAS_MAX_SCALE = 2;
const FLY = { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const };
/**
 * Wheel input is ignored while a focus-control move plays: trackpad inertia keeps sending
 * wheel events for a moment after the fingers lift, and would otherwise cancel the move.
 */
const FLY_WHEEL_GUARD_MS = 650;

const clampScale = (s: number) => Math.min(CANVAS_MAX_SCALE, Math.max(CANVAS_MIN_SCALE, s));

export function unionRects(rects: Array<WorldRect | null | undefined>): WorldRect | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    if (!r || !(r.w > 0 || r.h > 0)) continue;
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w);
    maxY = Math.max(maxY, r.y + r.h);
  }
  return minX === Infinity ? null : { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Innermost element under `target` (within `root`) that can still scroll in the wheel's direction. */
function scrollableUnder(target: Element | null, root: Element, dx: number, dy: number): boolean {
  let el: Element | null = target;
  while (el && el !== root) {
    if (el instanceof HTMLElement) {
      const cs = getComputedStyle(el);
      const canY = /(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1;
      const canX = /(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 1;
      if (canY && dy !== 0) {
        if ((dy > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) || (dy < 0 && el.scrollTop > 0)) return true;
      }
      if (canX && dx !== 0) {
        if ((dx > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 1) || (dx < 0 && el.scrollLeft > 0)) return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}

function isEditableTarget(t: EventTarget | null): boolean {
  const el = t instanceof Element ? t : null;
  return !!el?.closest("input, textarea, select, [contenteditable='true'], [contenteditable='']");
}

/** Pointer-downs on these never start a canvas pan — they belong to journal content and tools. */
const NO_PAN_SELECTOR =
  "[data-canvas-item], button, a, input, textarea, select, label, [contenteditable='true'], [contenteditable='']";

/**
 * Desktop infinite canvas: a pannable, zoomable viewport onto an unbounded world.
 *
 * The world is a zero-size origin point. Every object is absolutely positioned from it
 * in world px and may sit at any positive or negative coordinate — there is no world
 * box, so nothing is clipped and panning isn't limited to a page. The viewport only
 * applies translate + scale. `referenceWidth` is used solely to frame the initial view
 * around the default layout (which is authored at that width); it bounds nothing.
 * Items that are dragged work out the current zoom from their own rendered size, so
 * no zoom state has to be threaded through them.
 *
 * Input model — chosen so canvas navigation never competes with editing:
 * - wheel / two-finger trackpad scroll pans (unless over something scrollable)
 * - ctrl/⌘ + wheel and trackpad pinch zoom around the pointer
 * - dragging empty canvas pans; dragging content, text, or tools never does
 * - hold Space (outside text fields) or use the middle button to pan from anywhere
 */
export const JournalCanvas = forwardRef<JournalCanvasApi, {
  /** Width the default layout is authored at (world x 0…referenceWidth); frames the initial view only. */
  referenceWidth: number;
  children: React.ReactNode;
  /** Items to step through with the focus control, in order. */
  getFocusTargets: () => FocusTarget[];
  /** Extra world-space content bounds not represented by DOM items (e.g. sketch strokes). */
  getExtraBounds?: () => WorldRect | null;
  getInsets?: () => ViewInsets;
  /** While true, left-drag on empty canvas is left to the sketch layer (no pan). */
  sketchActive?: boolean;
  /** Fired ~150ms after the view stops moving, with the visible world rect. */
  onViewSettled?: (view: WorldRect) => void;
  showPaper?: boolean;
  className?: string;
}>(function JournalCanvas({
  referenceWidth, children, getFocusTargets, getExtraBounds, getInsets, sketchActive, onViewSettled, showPaper = true, className,
}, ref) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);

  // Focus-control cycle resets whenever the user navigates by hand. Only the first manual
  // move after a focus-control move re-renders, not every wheel event.
  const [manualTick, setManualTick] = useState(0);
  const cycleActive = useRef(false);
  const interacted = useRef(false);
  const markManual = useCallback(() => {
    interacted.current = true;
    x.stop(); y.stop(); scale.stop();
    if (cycleActive.current) {
      cycleActive.current = false;
      setManualTick((n) => n + 1);
    }
  }, [x, y, scale]);

  // Initial view: the default layout's reference width centred in the window, at 100%
  // when the window is at least that wide (so journals open looking as they always
  // have) and scaled down to fit when it's narrower. Until the user navigates, keep
  // that framing on resize.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const frameInitial = () => {
      if (interacted.current) return;
      const s = clampScale(Math.min(1, vp.clientWidth / referenceWidth));
      scale.set(s);
      x.set(Math.round((vp.clientWidth - referenceWidth * s) / 2));
      y.set(0);
    };
    frameInitial();
    const ro = new ResizeObserver(frameInitial);
    ro.observe(vp);
    return () => ro.disconnect();
  }, [referenceWidth, x, y, scale]);

  // ── Geometry ────────────────────────────────────────────────────────────────
  const getViewWorldRect = useCallback((): WorldRect => {
    const vp = viewportRef.current;
    const s = scale.get();
    const w = vp?.clientWidth ?? 0;
    const h = vp?.clientHeight ?? 0;
    return { x: -x.get() / s, y: -y.get() / s, w: w / s, h: h / s };
  }, [x, y, scale]);

  const clientToWorld = useCallback((cx: number, cy: number) => {
    const vp = viewportRef.current!.getBoundingClientRect();
    const s = scale.get();
    return { x: (cx - vp.left - x.get()) / s, y: (cy - vp.top - y.get()) / s };
  }, [x, y, scale]);

  const clientRectToWorld = useCallback((r: DOMRect): WorldRect => {
    const vp = viewportRef.current!.getBoundingClientRect();
    const s = scale.get();
    return {
      x: (r.left - vp.left - x.get()) / s,
      y: (r.top - vp.top - y.get()) / s,
      w: r.width / s,
      h: r.height / s,
    };
  }, [x, y, scale]);

  const getContentBounds = useCallback((): WorldRect | null => {
    const world = worldRef.current;
    if (!world) return null;
    const rects: WorldRect[] = [];
    world.querySelectorAll<HTMLElement>("[data-canvas-key]").forEach((el) => {
      if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") return; // display:none
      rects.push(clientRectToWorld(el.getBoundingClientRect()));
    });
    return unionRects([...rects, getExtraBounds?.() ?? null]);
  }, [clientRectToWorld, getExtraBounds]);

  useImperativeHandle(ref, () => ({ getViewWorldRect, clientToWorld, clientRectToWorld, getContentBounds }), [
    getViewWorldRect, clientToWorld, clientRectToWorld, getContentBounds,
  ]);

  // ── View changes ────────────────────────────────────────────────────────────
  const flyingUntil = useRef(0);
  const setView = useCallback((tx: number, ty: number, s: number, smooth: boolean) => {
    if (!smooth) {
      x.set(tx); y.set(ty); scale.set(s);
      return;
    }
    interacted.current = true; // a framed view shouldn't be re-centred by a later resize
    flyingUntil.current = performance.now() + FLY_WHEEL_GUARD_MS;
    animate(x, tx, FLY);
    animate(y, ty, FLY);
    animate(scale, s, FLY);
  }, [x, y, scale]);

  /** Zoom to `next` keeping the screen point (px, py) — relative to the viewport — fixed. */
  const zoomAt = useCallback((next: number, px: number, py: number, smooth = false) => {
    const s = scale.get();
    const s2 = clampScale(next);
    const k = s2 / s;
    setView(px - (px - x.get()) * k, py - (py - y.get()) * k, s2, smooth);
  }, [scale, x, y, setView]);

  const frame = useCallback((rect: WorldRect, mode: "fit" | "focus") => {
    const vp = viewportRef.current;
    if (!vp) return;
    const ins = getInsets?.() ?? { top: 24, right: 32, bottom: 120, left: 32 };
    const availW = Math.max(120, vp.clientWidth - ins.left - ins.right);
    const availH = Math.max(120, vp.clientHeight - ins.top - ins.bottom);
    const fit = Math.min(availW / rect.w, availH / rect.h);
    // Fit shows everything (never zooming past 100%); focus brings one item to reading
    // size, only shrinking — and not below 60% — when it can't fit at 100%.
    const s = mode === "fit" ? clampScale(Math.min(1, fit)) : clampScale(Math.min(1, Math.max(0.6, fit)));
    const tx = ins.left + (availW - rect.w * s) / 2 - rect.x * s;
    const tooTall = rect.h * s > availH;
    const ty = tooTall ? ins.top - rect.y * s : ins.top + (availH - rect.h * s) / 2 - rect.y * s;
    setView(tx, ty, s, true);
  }, [getInsets, setView]);

  const fitContent = useCallback(() => {
    const b = getContentBounds();
    if (b) frame(b, "fit");
  }, [getContentBounds, frame]);

  const focusKey = useCallback((key: string): boolean => {
    const el = worldRef.current?.querySelector<HTMLElement>(`[data-canvas-key="${CSS.escape(key)}"]`);
    if (!el) return false;
    frame(clientRectToWorld(el.getBoundingClientRect()), "focus");
    return true;
  }, [frame, clientRectToWorld]);

  // Debounced "view settled" notification (used to extend the sketch surface).
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const notifySettled = useCallback(() => {
    if (!onViewSettled) return;
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => onViewSettled(getViewWorldRect()), 150);
  }, [onViewSettled, getViewWorldRect]);
  useMotionValueEvent(x, "change", notifySettled);
  useMotionValueEvent(y, "change", notifySettled);
  useMotionValueEvent(scale, "change", notifySettled);
  useEffect(() => () => clearTimeout(settleTimer.current), []);

  // ── Wheel + Safari pinch (non-passive so the browser's own zoom/back-swipe is suppressed) ──
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      if (performance.now() < flyingUntil.current) { e.preventDefault(); return; }
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? vp.clientHeight : 1;
      let dx = e.deltaX * unit;
      let dy = e.deltaY * unit;
      const r = vp.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        markManual();
        // Trackpad pinches send small deltas; a mouse-wheel notch is ~100 — cap each step
        // (≈ ±26%) so one notch doesn't jump straight to the zoom limit.
        const step = Math.max(-30, Math.min(30, dy));
        zoomAt(scale.get() * Math.exp(-step * 0.01), e.clientX - r.left, e.clientY - r.top);
        return;
      }
      if (e.shiftKey && dx === 0) { dx = dy; dy = 0; }
      // Let genuinely scrollable content (a long textarea, a scrolling panel) scroll first.
      if (scrollableUnder(e.target as Element, vp, dx, dy)) return;
      e.preventDefault();
      markManual();
      x.set(x.get() - dx);
      y.set(y.get() - dy);
    };
    // Safari reports trackpad pinch as gesture events rather than ctrl+wheel.
    let gestureStart = 1;
    const onGestureStart = (e: Event) => { e.preventDefault(); gestureStart = scale.get(); markManual(); };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const ge = e as Event & { scale: number; clientX: number; clientY: number };
      const r = vp.getBoundingClientRect();
      zoomAt(gestureStart * ge.scale, ge.clientX - r.left, ge.clientY - r.top);
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    vp.addEventListener("gesturestart", onGestureStart);
    vp.addEventListener("gesturechange", onGestureChange);
    return () => {
      vp.removeEventListener("wheel", onWheel);
      vp.removeEventListener("gesturestart", onGestureStart);
      vp.removeEventListener("gesturechange", onGestureChange);
    };
  }, [markManual, zoomAt, scale, x, y]);

  // ── Space-to-pan (ignored while typing) ────────────────────────────────────
  const spaceDown = useRef(false);
  const [spacePan, setSpacePan] = useState(false);
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat || isEditableTarget(e.target) || isEditableTarget(document.activeElement)) return;
      if (e.target instanceof HTMLButtonElement) return; // Space activates focused buttons
      e.preventDefault();
      spaceDown.current = true;
      setSpacePan(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      spaceDown.current = false;
      setSpacePan(false);
    };
    const blur = () => { spaceDown.current = false; setSpacePan(false); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, []);

  // ── Drag-to-pan ─────────────────────────────────────────────────────────────
  const [panning, setPanning] = useState(false);
  /** Whether a press here belongs to the canvas (pan) rather than to content or tools. */
  const isPanPress = useCallback((e: { button: number; target: EventTarget | null }) => {
    if (e.button === 1 || (e.button === 0 && spaceDown.current)) return true;
    if (e.button !== 0 || sketchActive) return false;
    return !(e.target as Element).closest(NO_PAN_SELECTOR);
  }, [sketchActive]);

  // A canvas press must not start a native text selection across the cards. That also
  // skips the browser's usual click-away behaviour, so do it here: clear any selection
  // and leave any open text field.
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPanPress(e)) return;
    e.preventDefault();
    window.getSelection()?.removeAllRanges();
    const active = document.activeElement;
    if (active instanceof HTMLElement && isEditableTarget(active)) active.blur();
  }, [isPanPress]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanPress(e)) return;
    const forced = e.button === 1 || spaceDown.current;
    // Nothing is claimed until the pointer actually moves, so a plain click on empty
    // canvas still behaves as a click (e.g. deselecting the current section).
    const sx = e.clientX, sy = e.clientY, ox = x.get(), oy = y.get();
    let started = false;
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (!started) {
        if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
        started = true;
        markManual();
        setPanning(true);
        window.getSelection()?.removeAllRanges();
      }
      x.set(ox + dx);
      y.set(oy + dy);
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      setPanning(false);
    };
    if (forced) e.preventDefault();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }, [isPanPress, markManual, x, y]);

  // ── Zoom buttons ────────────────────────────────────────────────────────────
  const zoomAroundCenter = useCallback((next: number) => {
    const vp = viewportRef.current;
    if (!vp) return;
    markManual();
    zoomAt(next, vp.clientWidth / 2, vp.clientHeight / 2, true);
  }, [markManual, zoomAt]);

  // Paper texture moves and scales with the canvas so panning reads as moving over paper.
  const dot = useTransform(scale, (s) => `${22 * s}px ${22 * s}px, 100% 100%`);
  const paperPos = useMotionTemplate`${x}px ${y}px, 0px 0px`;

  return (
    <div
      ref={viewportRef}
      className={"relative overflow-hidden " + (className ?? "")}
      style={{
        touchAction: "none",
        cursor: panning ? "grabbing" : spacePan ? "grab" : undefined,
        userSelect: panning ? "none" : undefined,
      }}
      onPointerDown={handlePointerDown}
      onMouseDown={handleMouseDown}
      onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}
    >
      {showPaper && (
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "#eeeeec",
            backgroundImage:
              "radial-gradient(circle, rgba(150,147,142,0.22) 1px, transparent 1px), " +
              "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.5), transparent 60%)",
            backgroundSize: dot as unknown as MotionValue<string>,
            backgroundPosition: paperPos,
          }}
        />
      )}
      <motion.div
        ref={worldRef}
        data-canvas-world
        className="absolute left-0 top-0"
        // Zero-size origin: children are positioned from here and overflow freely.
        style={{ x, y, scale, originX: 0, originY: 0, width: 0, height: 0 }}
      >
        {children}
      </motion.div>

      <CanvasControls
        scale={scale}
        resetSignal={manualTick}
        onNavigated={() => { cycleActive.current = true; }}
        getTargets={getFocusTargets}
        onFit={fitContent}
        onFocusKey={focusKey}
        onZoomIn={() => zoomAroundCenter(scale.get() * 1.25)}
        onZoomOut={() => zoomAroundCenter(scale.get() / 1.25)}
        onZoomReset={() => zoomAroundCenter(1)}
      />
    </div>
  );
});
